import React from 'react';
import { FormattedMessage } from 'react-intl';
import Button from '../core/Button';
import '@mdxeditor/editor/style.css';
import {
  MDXEditor,
  MDXEditorMethods,
  UndoRedo,
  BoldItalicUnderlineToggles,
  toolbarPlugin,
  BlockTypeSelect,
  headingsPlugin,
  linkPlugin,
  listsPlugin,
  ListsToggle,
  quotePlugin,
  markdownShortcutPlugin,
} from '@mdxeditor/editor';
import { MarkdownViewer } from './MarkdownViewer';
import { IconCheck } from '@/icons';
import {
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';

interface MarkdownEditorProps<
  T extends FieldValues,
> extends UseControllerProps<T> {
  isOpen: boolean;
  onCancel: () => void;
}

export const MarkdownEditor = <T extends FieldValues>({
  onCancel,
  isOpen,
  ...props
}: MarkdownEditorProps<T>) => {
  const ref = React.useRef<MDXEditorMethods>(null);
  const { field } = useController(props);

  if (!isOpen) {
    return <MarkdownViewer markdown={field.value} />;
  }

  return (
    <div className="relative">
      <div className="min-h-[200px]">
        <MDXEditor
          ref={ref}
          className="text-text-950"
          markdown={field.value || ''}
          plugins={[
            headingsPlugin(),
            listsPlugin(),
            quotePlugin(),
            linkPlugin(),
            markdownShortcutPlugin(),
            toolbarPlugin({
              toolbarContents: () => (
                <>
                  <BoldItalicUnderlineToggles />
                  <BlockTypeSelect />
                  <ListsToggle />
                  <UndoRedo />
                </>
              ),
            }),
          ]}
          onChange={field.onChange}
          contentEditableClassName="max-h-[450px] overflow-y-auto bg-background-50 text-text-950"
          autoFocus
        />
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>
          <FormattedMessage defaultMessage="Avbryt" />
        </Button>
        <Button type="submit" variant="filled" startIcon={<IconCheck />}>
          <FormattedMessage defaultMessage="Spara" />
        </Button>
      </div>
    </div>
  );
};
